// import external libraries
import dayjs from 'dayjs';

// import utils
import { getWeekFieldName } from 'components/shared/RosterViewDataGrid/hooks/useRosterTimelineColumns/utils';

// import constants
import { BACKLOG_CATEGORIES } from '../../Backlog.consts';

// import types
import type {
  BackLogResponseList,
  ClientResponse,
  EventResponse
} from 'generated/index';
import type {
  RosterDay,
  WeekValue
} from 'components/shared/RosterViewDataGrid';
import type {
  FilterOption,
  BacklogFilterState,
  BacklogRow,
  BacklogTaskCategory
} from '../../Backlog.types';

export const ROW_HEIGHT = 44;
export const TIMELINE_COLUMN_WIDTH = 100;

/** Prefix that distinguishes a care receiver header row from the rows underneath it. */
const CARE_RECEIVER_ROW_ID_PREFIX = 'careReceiver:';

type WeekBucket = {
  field: string;
  start: number;
  end: number;
};

// buildBacklogRows and buildDisplayRows both derive their week columns from the same `weeks`, which
// the hook memoises and only rebuilds when the window changes. Cache on the array reference so the
// second call reuses the first result rather than recomputing it — and so the two can never derive
// different fields from the same weeks.
const weekBucketsCache = new WeakMap<RosterDay[][], WeekBucket[]>();

/**
 * Derives the grid's week columns from the roster days the grid itself renders, so summaries can
 * be matched to columns by date containment. Recomputing ISO week numbers here instead would
 * drift from the grid's own columns around year boundaries.
 */
export const getWeekBuckets = (weeks: RosterDay[][]): WeekBucket[] => {
  const cached = weekBucketsCache.get(weeks);
  if (cached) return cached;

  const buckets = weeks
    .filter((week) => week.length > 0)
    .map((week) => ({
      field: getWeekFieldName(week[0].weekNr, week[0].date.getFullYear()),
      start: dayjs(week[0].date).startOf('day').valueOf(),
      end: dayjs(week[week.length - 1].date)
        .endOf('day')
        .valueOf()
    }));

  weekBucketsCache.set(weeks, buckets);
  return buckets;
};

const findBucketIndex = (buckets: WeekBucket[], isoDate: string): number => {
  // dayjs parses 'YYYY-MM-DD' as local midnight; new Date() would parse it as UTC and shift a
  // day in timezones behind UTC.
  const timestamp = dayjs(isoDate).startOf('day').valueOf();
  return buckets.findIndex(
    (bucket) => timestamp >= bucket.start && timestamp <= bucket.end
  );
};

/**
 * Flattens the backlog response into one row per (care receiver, prescription rule) and writes
 * each period summary into the week column it belongs to.
 *
 * `PeriodSummary.plannedAppointments` becomes `WeekValue.current` and
 * `prescribedAppointments` becomes `WeekValue.total`, so `WeekValueCell` renders
 * `current - total` — the same value the backend reports as `difference`.
 */
export const buildBacklogRows = (
  backlog: BackLogResponseList | undefined,
  weeks: RosterDay[][]
): BacklogRow[] => {
  const buckets = getWeekBuckets(weeks);

  if (!backlog?.length || !buckets.length) return [];

  const windowStart = buckets[0].start;
  const windowEnd = buckets[buckets.length - 1].end;

  const emptyWeekData = Object.fromEntries(
    buckets.map((bucket) => [bucket.field, null])
  );

  const rows = backlog.flatMap((entry) => {
    // The planning entity is an untyped object in the contract; for care receivers it is a client.
    const careReceiver = entry.planningEntity as ClientResponse;

    const deviationRows = entry.prescriptionRuleSummaries.map((ruleSummary) => {
      const weekData: Record<string, WeekValue | null> = { ...emptyWeekData };

      let totalPlanned = 0;
      let totalPrescribed = 0;
      let hasDeviation = false;

      for (const periodSummary of ruleSummary.periodSummaries) {
        // A summary is not always a single week: a TOTAL period asks for N appointments anywhere in
        // its window and is reported as one summary spanning that whole window — which can start
        // before the visible weeks and/or end after them while still overlapping them.
        const periodStart = dayjs(periodSummary.startPeriod)
          .startOf('day')
          .valueOf();
        const periodEnd = dayjs(periodSummary.endPeriod)
          .startOf('day')
          .valueOf();

        // Drop only a period that does not overlap the window at all. Clamping just the tail (as an
        // earlier version did) silently discarded a period that overlaps from before the window, and
        // the row then read as fully planned and dropped out of the task list entirely.
        //
        // A clamped period still contributes its whole-period counts: the summary reports one pair of
        // numbers for its entire window, with no per-week split to prorate from. So a TOTAL period
        // reaching beyond the visible weeks adds all of its work to the row's Totaal and shows it in
        // the first visible week — deliberately, since counting nothing would read as fully planned.
        if (periodEnd < windowStart || periodStart > windowEnd) continue;

        const rawFirst = findBucketIndex(buckets, periodSummary.startPeriod);
        const rawLast = findBucketIndex(buckets, periodSummary.endPeriod);
        const firstIndex = rawFirst === -1 ? 0 : rawFirst;
        const lastIndex = rawLast === -1 ? buckets.length - 1 : rawLast;
        const cellColSpan = Math.max(1, lastIndex - firstIndex + 1);

        weekData[buckets[firstIndex].field] = {
          current: periodSummary.plannedAppointments,
          total: periodSummary.prescribedAppointments,
          cellColSpan
        };

        // Null out the spanned weeks so they do not render a cell of their own.
        for (let index = firstIndex + 1; index <= lastIndex; index++) {
          weekData[buckets[index].field] = null;
        }

        totalPlanned += periodSummary.plannedAppointments;
        totalPrescribed += periodSummary.prescribedAppointments;
        // Both directions count: a surplus is as much a task as a shortage.
        if (periodSummary.difference !== 0) hasDeviation = true;
      }

      const { prescriptionRule } = ruleSummary;

      return {
        id: `${careReceiver.id}:${prescriptionRule.id}`,
        careReceiverId: careReceiver.id,
        careReceiverName: careReceiver.displayName ?? '',
        careReceiverNumber: careReceiver.number,
        activityName: prescriptionRule.activity.name,
        activityCode: prescriptionRule.activity.code,
        description: prescriptionRule.description,
        totalPlanned,
        totalPrescribed,
        totalDifference: totalPlanned - totalPrescribed,
        hasDeviation,
        category: 'deviation' as const,
        rowType: 'rule' as const,
        ...weekData
      };
    });

    // Appointments this care receiver has that no prescription rule covers, grouped by activity —
    // internal rows the weeks-view aggregates into one non-folding "Afspraken buiten behandeling" row
    // (see buildDisplayRows). Kept per activity so the toolbar's activity filter still narrows them.
    // Each week cell is a plain count of that activity's stray appointments; they carry no
    // planned/prescribed pair, so they always count as a task (hasDeviation).
    const eventsByActivityCode = new Map<
      string,
      { activityName: string; dates: string[] }
    >();
    for (const event of entry.outsideTreatmentEvents ?? []) {
      const { code, name } = event.activity;
      const existing = eventsByActivityCode.get(code);
      if (existing) {
        existing.dates.push(event.date);
      } else {
        eventsByActivityCode.set(code, {
          activityName: name,
          dates: [event.date]
        });
      }
    }

    const outsideTreatmentRows = [...eventsByActivityCode.entries()].map(
      ([activityCode, { activityName, dates }]) => {
        const weekData: Record<string, number | null> = { ...emptyWeekData };

        let strayCount = 0;
        for (const date of dates) {
          const bucketIndex = findBucketIndex(buckets, date);
          if (bucketIndex === -1) continue;
          const field = buckets[bucketIndex].field;
          weekData[field] = ((weekData[field] as number | null) ?? 0) + 1;
          strayCount += 1;
        }

        return {
          id: `${careReceiver.id}:outsideTreatment:${activityCode}`,
          careReceiverId: careReceiver.id,
          careReceiverName: careReceiver.displayName ?? '',
          careReceiverNumber: careReceiver.number,
          activityName,
          activityCode,
          description: '',
          totalPlanned: 0,
          totalPrescribed: 0,
          totalDifference: 0,
          // Always a task: an appointment outside treatment is something to put right, so it is never
          // pruned as "fully planned".
          hasDeviation: true,
          // The number of stray appointments this row stands for — summed into the tile count and the
          // aggregated category row.
          outstanding: strayCount,
          category: 'outsideTreatment' as const,
          rowType: 'rule' as const,
          ...weekData
        };
      }
    );

    return [...deviationRows, ...outsideTreatmentRows];
  });

  return rows.sort(
    (a, b) =>
      a.careReceiverName.localeCompare(b.careReceiverName) ||
      // Tie-break on the id so two care receivers sharing a name cannot interleave, which would
      // give buildDisplayRows two headers for the same block.
      a.careReceiverId.localeCompare(b.careReceiverId) ||
      a.activityName.localeCompare(b.activityName)
  );
};

/** Identifies a category row, and with it what the collapse state applies to. */
export const getCategoryRowKey = (
  careReceiverId: string,
  category: BacklogTaskCategory
) => `${careReceiverId}:${category}`;

const emptyRowFields = {
  activityName: '',
  activityCode: '',
  description: '',
  totalPlanned: 0,
  totalPrescribed: 0,
  totalDifference: 0,
  hasDeviation: false
};

/**
 * Totals the week cells of a set of rule rows into what a category row shows: how much there is to do
 * that week.
 *
 * Absolute values, not a net. A rule short one appointment and a rule over by one are two things to
 * put right, not nothing — netting them would report zero and, worse, render the checkmark that says
 * the week is already met.
 *
 * The result is a plain number rather than a planned/prescribed pair, because a magnitude is what it
 * is: there is no pair of counts whose difference means "four things to fix".
 *
 * A value that spans several weeks — a TOTAL period — lands entirely on the week it starts in,
 * because that is where the rule row carries it. The aggregate therefore shows a spanned obligation
 * at its start rather than smeared across the span.
 */
/**
 * The work a single week cell stands for, as a non-negative magnitude: an outside-treatment cell
 * carries a plain count and adds straight in; a deviation cell carries a planned/prescribed pair whose
 * gap is the work to do. Anything else (empty cell) is zero. Shared so the grid's category totals and
 * the week-detail's category value are derived the same way.
 */
export const getWeekValueMagnitude = (
  value: string | number | boolean | WeekValue | null | undefined
): number => {
  if (typeof value === 'number') return value;
  if (!value || typeof value !== 'object') return 0;
  return Math.abs(value.current - value.total);
};

const sumOutstandingByWeek = (
  rows: BacklogRow[],
  weekFields: string[]
): Record<string, number | null> => {
  const summed: Record<string, number | null> = {};

  weekFields.forEach((field) => {
    let outstanding = 0;
    let hasValue = false;

    rows.forEach((row) => {
      const value = row[field];
      // Only a number or a week-value pair contributes; an empty or non-value cell leaves hasValue as
      // is, so a week no row reports on stays null rather than a misleading zero.
      if (typeof value !== 'number' && (!value || typeof value !== 'object')) {
        return;
      }
      outstanding += getWeekValueMagnitude(value);
      hasValue = true;
    });

    summed[field] = hasValue ? outstanding : null;
  });

  return summed;
};

/**
 * Turns the flat list of rule rows into what the grid renders: a care receiver row, then one row per
 * task category found for that care receiver carrying the totals of its rules, then — unless the
 * category is collapsed — the rules themselves.
 *
 * Categories keep the order of {@link BACKLOG_CATEGORIES} so they do not jump around between
 * refreshes. Note this is the constant order, not the tile strip's order: the tiles are sorted by
 * label (implemented first) in BacklogCategoryTiles, so with three or more categories the row order
 * and the tile order can differ.
 *
 * Expects the rows to be grouped by care receiver already — {@link buildBacklogRows} sorts them that
 * way and filtering preserves the order. Run this *after* filtering, otherwise a category whose every
 * rule was filtered out would leave an orphaned header behind.
 */
export const buildDisplayRows = (
  ruleRows: BacklogRow[],
  weeks: RosterDay[][],
  collapsedCategoryKeys: Set<string> = new Set()
): BacklogRow[] => {
  if (!ruleRows.length) return [];

  const weekFields = getWeekBuckets(weeks).map(({ field }) => field);
  const displayRows: BacklogRow[] = [];

  const careReceiverOrder: string[] = [];
  const rowsByCareReceiver = new Map<string, BacklogRow[]>();

  ruleRows.forEach((row) => {
    const existing = rowsByCareReceiver.get(row.careReceiverId);
    if (existing) {
      existing.push(row);
      return;
    }
    careReceiverOrder.push(row.careReceiverId);
    rowsByCareReceiver.set(row.careReceiverId, [row]);
  });

  careReceiverOrder.forEach((careReceiverId) => {
    const careReceiverRows = rowsByCareReceiver.get(careReceiverId) ?? [];
    const [{ careReceiverName, careReceiverNumber }] = careReceiverRows;

    displayRows.push({
      ...emptyRowFields,
      id: `${CARE_RECEIVER_ROW_ID_PREFIX}${careReceiverId}`,
      rowType: 'careReceiver',
      careReceiverId,
      careReceiverName,
      careReceiverNumber,
      category: careReceiverRows[0].category
    });

    BACKLOG_CATEGORIES.forEach(({ category }) => {
      const categoryRows = careReceiverRows.filter(
        (row) => row.category === category
      );
      if (!categoryRows.length) return;

      // Outside treatment is a single, non-folding row: its week cells are opened per day in the
      // week-detail view instead of expanded here, so it carries no rule children and no chevron.
      const isOutsideTreatment = category === 'outsideTreatment';

      const key = getCategoryRowKey(careReceiverId, category);
      const isExpanded = !collapsedCategoryKeys.has(key);
      const totalPlanned = categoryRows.reduce(
        (sum, row) => sum + row.totalPlanned,
        0
      );
      const totalPrescribed = categoryRows.reduce(
        (sum, row) => sum + row.totalPrescribed,
        0
      );
      const weekOutstanding = sumOutstandingByWeek(categoryRows, weekFields);
      // Summed from the very cells shown beside it, so the Total can never contradict the weeks. Note
      // this is not |totalPlanned - totalPrescribed|: a rule short a week and over the next nets out
      // over the period, while the work to do is one in each of those weeks.
      const outstanding = Object.values(weekOutstanding).reduce<number>(
        (sum, value) => sum + (value ?? 0),
        0
      );

      displayRows.push({
        ...emptyRowFields,
        id: key,
        rowType: 'category',
        careReceiverId,
        careReceiverName,
        category,
        // Deviations count their rules; outside treatment counts its appointments (its Total).
        taskCount: isOutsideTreatment ? outstanding : categoryRows.length,
        isExpanded: isOutsideTreatment ? false : isExpanded,
        // Kept as the honest signed net for anything that wants it; the Total cell shows `outstanding`.
        totalPlanned,
        totalPrescribed,
        totalDifference: totalPlanned - totalPrescribed,
        outstanding,
        hasDeviation: categoryRows.some((row) => row.hasDeviation),
        ...weekOutstanding
      });

      if (!isOutsideTreatment && isExpanded) displayRows.push(...categoryRows);
    });
  });

  return displayRows;
};

/**
 * The toolbar's dropdowns, as one predicate. Shared with {@link getTaskCategoryCounts} so a tile can
 * never count a row the grid would hide — an empty selection means "no restriction", not "nothing".
 */
const matchesDropdownFilters = (
  row: BacklogRow,
  { activityCodes, careReceiverIds }: BacklogFilterState
): boolean =>
  (!activityCodes.length || activityCodes.includes(row.activityCode)) &&
  (!careReceiverIds.length || careReceiverIds.includes(row.careReceiverId));

/**
 * Client-side filters.
 *
 * The page is a task list, so by default a row without a deviation is not listed — the request
 * already asks for deviating weeks only, but the backend prunes on the requested period while the
 * grid shows a window of weeks, so the row check still has to run here. When "Fully planned" is on
 * (showCompleted), the fulfilled rows are kept as well, matching the weeks the backend then returns.
 */
export const filterBacklogRows = (
  rows: BacklogRow[],
  filters: BacklogFilterState
): BacklogRow[] =>
  rows.filter((row) => {
    if (!filters.showCompleted && !row.hasDeviation) return false;
    if (!filters.activeCategories.includes(row.category)) return false;
    return matchesDropdownFilters(row, filters);
  });

/**
 * How many tasks each tile stands for. For deviations one task is one prescription rule that deviates
 * somewhere in the visible period — the unit the planner sees as a row. For outside treatment one task
 * is one stray appointment (a card in the week view), so its rows count by their appointment total
 * rather than one-per-row.
 *
 * Counts are taken after the toolbar's dropdowns but before the tile filter, so a tile always shows
 * what selecting it would bring in. activeCategories on the filters passed in is ignored here for that
 * reason — {@link matchesDropdownFilters} does not read it.
 */
export const getTaskCategoryCounts = (
  rows: BacklogRow[],
  filters: BacklogFilterState
): Partial<Record<BacklogTaskCategory, number>> => {
  const counts: Partial<Record<BacklogTaskCategory, number>> = {};

  rows.forEach((row) => {
    if (!row.hasDeviation) return;
    if (!matchesDropdownFilters(row, filters)) return;
    const increment =
      row.category === 'outsideTreatment' ? row.outstanding ?? 0 : 1;
    counts[row.category] = (counts[row.category] ?? 0) + increment;
  });

  return counts;
};

/**
 * The care receivers the response holds, for the toolbar's second dropdown. Deduplicated by
 * identifier rather than by name, so two care receivers who share a name both stay selectable — the
 * backlog carries no client number to tell them apart in the label.
 */
export const getCareReceiverOptions = (
  backlog: BackLogResponseList | undefined
): FilterOption[] => {
  const optionsById = new Map<string, FilterOption>();

  backlog?.forEach((entry) => {
    const { id, displayName } = entry.planningEntity as ClientResponse;
    if (!optionsById.has(id)) {
      optionsById.set(id, { id, name: displayName ?? '' });
    }
  });

  return [...optionsById.values()].sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Activity options for the filter, derived from the response — there is no activity list
 * endpoint scoped to the selected care receivers.
 */
export const getActivityOptions = (
  backlog: BackLogResponseList | undefined
): FilterOption[] => {
  const optionsByCode = new Map<string, FilterOption>();

  const addActivity = ({ code, name }: { code: string; name: string }) => {
    if (!optionsByCode.has(code)) {
      optionsByCode.set(code, { id: code, name: `${name} - ${code}` });
    }
  };

  backlog?.forEach((entry) => {
    entry.prescriptionRuleSummaries.forEach(({ prescriptionRule }) =>
      addActivity(prescriptionRule.activity)
    );
    // Outside-treatment activities need not be prescribed at all, so they would otherwise be missing
    // from the filter — offer them too.
    entry.outsideTreatmentEvents?.forEach(({ activity }) =>
      addActivity(activity)
    );
  });

  return [...optionsByCode.values()].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
};

/**
 * The seven roster days of the week a cell field points at — the day columns of the week-detail view.
 * Matches the field back to its bucket, so the detail's days line up with the grid's week columns.
 *
 * The bucket index is reused to index `weeks.filter(week => week.length > 0)` directly: getWeekBuckets
 * builds its buckets from that same filtered list in the same order, so the two stay aligned — keep it
 * so if either side's filtering ever changes.
 */
export const getWeekDays = (
  weeks: RosterDay[][],
  weekField: string
): RosterDay[] => {
  const bucketIndex = getWeekBuckets(weeks).findIndex(
    ({ field }) => field === weekField
  );
  if (bucketIndex === -1) return [];
  return weeks.filter((week) => week.length > 0)[bucketIndex] ?? [];
};

/**
 * The outside-treatment events that fall in the given week, narrowed by the toolbar's activity filter
 * so the week-detail honours the same filtering as the weeks view. The care-receiver filter is applied
 * a level up, where the week-detail decides which care receivers to show.
 */
export const selectWeekEvents = (
  events: EventResponse[] | undefined,
  weekDays: RosterDay[],
  activityCodes: string[]
): EventResponse[] =>
  (events ?? []).filter(
    (event) =>
      (!activityCodes.length || activityCodes.includes(event.activity.code)) &&
      weekDays.some((day) => dayjs(event.date).isSame(day.date, 'day'))
  );
